import React, { useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { useCalcMethods } from '../../hooks/useCalcMethods';
import type { Holding } from '../../types/Holding';
import { t } from '../../i18n/t';
import './CalcDetailsModal.css';

export interface CalcDetailsModalProps {
  row: Holding;
  onClose: () => void;
}

// Identifier-shape regex: a single word token (letters/digits/underscore,
// must start with a letter or _). Used to find every `qty`, `rate4Valuation`,
// `crcyRate4Valuation`, `multiplier`, etc. in a SQL formula string.
const IDENTIFIER_RE = /\b[a-zA-Z_][a-zA-Z0-9_]*\b/g;

// Format numbers with thousand separators (locale-independent — banking-tone:
// always western digits, comma thousand separators, dot decimal). No fixed
// precision: render whatever the JS Number toString produces, with grouping.
function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  // Split into integer + fractional and group the integer side with commas.
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const [int, frac] = String(abs).split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return frac ? `${sign}${grouped}.${frac}` : `${sign}${grouped}`;
}

// Substitute each identifier in the formula with the corresponding row value.
// Unknown identifiers (not on the row, or null) render as `?`.
function substituteFormula(formula: string, row: Record<string, unknown>): string {
  return formula.replace(IDENTIFIER_RE, (name) => {
    if (!Object.prototype.hasOwnProperty.call(row, name)) return '?';
    const v = row[name];
    if (v === null || v === undefined) return '?';
    if (typeof v === 'number') return formatNumber(v);
    return String(v);
  });
}

// "פירוט חישוב שיערוך" — read-only modal showing how the row's `valuation`
// was computed. Sourced from `useCalcMethods` (the existing module-cached
// catalog of valuation methods). Graceful when the row's calcMethodId is
// orphan (not in the catalog) or the formula text is NULL.
//
// Layout (RTL, banking-tone — restrained borders, no decorative color):
//   ┌─────────────────────────────────────────────┐
//   │ פירוט חישוב שיערוך                       ✕ │
//   ├─────────────────────────────────────────────┤
//   │ קוד שיטה: <code> (#<id>)                    │
//   │ שם: <name>                                  │
//   │                                             │
//   │ תיאור עברי                                  │
//   │   <calcMethodDetails or —>                  │
//   │                                             │
//   │ נוסחה                                       │
//   │   <formula (monospace) or warning>          │
//   │                                             │
//   │ הצבת ערכים                                  │
//   │   <substituted (monospace)>                 │
//   │                                             │
//   │ תוצאה                                       │
//   │   <formattedValuation>                      │
//   └─────────────────────────────────────────────┘
export function CalcDetailsModal({ row, onClose }: CalcDetailsModalProps) {
  const { data: catalog } = useCalcMethods();

  // Esc + click-outside close (matches LopSearchModal).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const method = useMemo(() => {
    if (!catalog || row.calcMethodId == null) return null;
    return catalog.find((m) => m.calcMethodId === row.calcMethodId) ?? null;
  }, [catalog, row.calcMethodId]);

  const noRow = t('holdings.calcDetails.noRow');

  // Substituted formula — only when both the formula text exists AND a row
  // we can read values from. We treat the Holding as a Record<string, unknown>
  // for the identifier-by-name substitution.
  const rowAsRecord = row as unknown as Record<string, unknown>;
  const formula = method?.formula ?? null;
  const substituted = formula ? substituteFormula(formula, rowAsRecord) : null;

  const handleBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="calc-details-modal__backdrop"
      onMouseDown={handleBackdropMouseDown}
    >
      <div
        className="calc-details-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('holdings.calcDetails.title')}
        dir="rtl"
      >
        <header className="calc-details-modal__header">
          <h2 className="calc-details-modal__title">
            {t('holdings.calcDetails.title')}
          </h2>
          <button
            type="button"
            className="calc-details-modal__close"
            aria-label={t('holdings.calcDetails.close')}
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </header>

        <div className="calc-details-modal__body">
          {/* Section 1 — method identity */}
          <dl className="calc-details-modal__identity">
            <div className="calc-details-modal__identity-row">
              <dt className="calc-details-modal__label">
                {t('holdings.calcDetails.methodCode')}
              </dt>
              <dd className="calc-details-modal__value">
                {method
                  ? `${method.calcMethodCode} (#${method.calcMethodId})`
                  : row.calcMethodId != null
                    ? `${t('holdings.calcDetails.methodNotFound')} #${row.calcMethodId}`
                    : noRow}
              </dd>
            </div>
            <div className="calc-details-modal__identity-row">
              <dt className="calc-details-modal__label">
                {t('holdings.calcDetails.methodName')}
              </dt>
              <dd className="calc-details-modal__value">
                {method?.calcMethodName ?? noRow}
              </dd>
            </div>
          </dl>

          {/* Section 2 — Hebrew description */}
          <section className="calc-details-modal__section">
            <h3 className="calc-details-modal__section-title">
              {t('holdings.calcDetails.hebrewDescription')}
            </h3>
            <div className="calc-details-modal__section-body">
              {method?.calcMethodDetails ?? noRow}
            </div>
          </section>

          {/* Section 3 — SQL formula */}
          <section className="calc-details-modal__section">
            <h3 className="calc-details-modal__section-title">
              {t('holdings.calcDetails.sqlFormula')}
            </h3>
            <div className="calc-details-modal__section-body calc-details-modal__formula">
              {formula ?? (
                <span className="calc-details-modal__warning">
                  {t('holdings.calcDetails.formulaNotDefined')}
                </span>
              )}
            </div>
          </section>

          {/* Section 4 — substituted values */}
          {substituted !== null && (
            <section className="calc-details-modal__section">
              <h3 className="calc-details-modal__section-title">
                {t('holdings.calcDetails.substituted')}
              </h3>
              <div className="calc-details-modal__section-body calc-details-modal__formula">
                {substituted}
              </div>
            </section>
          )}

          {/* Section 5 — result */}
          <section className="calc-details-modal__section">
            <h3 className="calc-details-modal__section-title">
              {t('holdings.calcDetails.result')}
            </h3>
            <div className="calc-details-modal__section-body calc-details-modal__result">
              {row.valuation == null ? noRow : formatNumber(row.valuation)}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
